import axios from "@/utils/axios";
import { useQuery } from "@tanstack/react-query";

// Interface for template variable definition
export interface TemplateVariable {
  type: string;
  required: boolean;
  description: string;
}

// Interface for email template from API
export interface EmailTemplate {
  id: string;
  type: string | null;
  name: string;
  description: string;
  subject: string;
  rich_text: string;
  variables: Record<string, TemplateVariable>;
  status: string;
  created_at: string;
  updated_at: string;
  required_variables: string[];
  optional_variables: string[];
}

// Interface for pagination links
export interface PaginationLink {
  url: string | null;
  label: string;
  page: number | null;
  active: boolean;
}

export interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  links: PaginationLink[];
  path: string;
  per_page: number;
  to: number;
  total: number;
}

// Interface for paginated response
export interface PaginatedResponse<T> {
  data: T[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: PaginationMeta;
}

// Query parameters interface
export interface GetEmailTemplatesParams {
  per_page?: number;
  page?: number;
  "filter[name]"?: string;
  "filter[status]"?: string;
}

/**
 * Hook to fetch the list of email templates
 *
 * @param params - Optional query parameters
 * @returns Query result with the list of email templates
 */
export const useGetEmailTemplates = (params?: GetEmailTemplatesParams) => {
  return useQuery({
    queryKey: ["email-templates", params],
    queryFn: async (): Promise<PaginatedResponse<EmailTemplate>> => {
      const response = await axios.get(`/admin/v1/email-templates`, {
        params,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch a specific email template by ID
 *
 * @param templateId - ID of the template to fetch
 * @returns Query result with the template data
 */
export const useGetEmailTemplateById = (templateId: string) => {
  return useQuery({
    queryKey: ["email-template", templateId],
    queryFn: async (): Promise<{ data: EmailTemplate }> => {
      const response = await axios.get(
        `/admin/v1/email-templates/${templateId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    },
    enabled: !!templateId, // Only execute if ID is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};
